// https://github.com/Grsmto/simplebar/tree/master/packages/simplebar-react
import { forwardRef, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SimpleBarJS from 'simplebar';
import 'simplebar/src/simplebar.css';

const SimpleBar = forwardRef(({ children, scrollableNodeProps = {}, ...props }, ref) => {
  let instance;
  let scrollableNodeRef = useRef(null);
  const elRef = useRef(null);
  const contentNodeRef = useRef(null);
  const options = {};
  const rest = {};

  Object.keys(props).forEach(key => {
    if (Object.prototype.hasOwnProperty.call(SimpleBarJS.defaultOptions, key)) {
      options[key] = props[key];
    } else {
      rest[key] = props[key];
    }
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    scrollableNodeRef = scrollableNodeProps.ref || scrollableNodeRef;

    if (elRef.current) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      instance = new SimpleBarJS(elRef.current, {
        ...options,
        ...(scrollableNodeRef && {
          scrollableNode: scrollableNodeRef.current,
        }),
        ...(contentNodeRef.current && {
          contentNode: contentNodeRef.current,
        }),
      });

      if (ref) {
        ref.current = instance;
      }
    }

    return () => {
      instance.unMount();
      instance = null;
    };
  }, []);

  return (
    <div ref={elRef} data-simplebar {...rest}>
      <div className="simplebar-wrapper">
        <div className="simplebar-height-auto-observer-wrapper">
          <div className="simplebar-height-auto-observer" />
        </div>
        <div className="simplebar-mask">
          <div className="simplebar-offset">
            {typeof children === 'function' ? (
              children({ scrollableNodeRef, contentNodeRef })
            ) : (
              <div
                {...scrollableNodeProps}
                className={classNames(
                  'simplebar-content-wrapper',
                  scrollableNodeProps.className,
                )}
              >
                <div className="simplebar-content">
                  {children}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="simplebar-placeholder" />
      </div>
      <div className="simplebar-track simplebar-horizontal">
        <div className="simplebar-scrollbar" />
      </div>
      <div className="simplebar-track simplebar-vertical">
        <div className="simplebar-scrollbar" />
      </div>
    </div>
  );
});

SimpleBar.displayName = 'SimpleBar';

SimpleBar.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
  ]),
  scrollableNodeProps: PropTypes.object,
};

export default SimpleBar;
